<template>
  <Layout :style="{ backgroundColor: 'var(--color-white)', minHeight }">
    <Sider
      hide-trigger
      :width="colSiderWidth"
      class="chart-properties-section"
    >
      <h3 style="margin-bottom: 16px">
        Chart Properties
      </h3>
      <div
        v-if="showIncompleteDataWarningMessage"
        style="font-size: 10px; margin-bottom: 16px"
      >
        {{ incompleteDataWarningMessage }}
      </div>
      <Form
        v-if="tableColumns.length"
        :model="currentChart"
        label-position="top"
      >
        <FormItem label="Type">
          <Select
            v-model="currentChart.type"
            @on-change="autoFillTitle"
          >
            <Option
              v-for="type in Object.keys(chartTypeNameMap)"
              :key="type"
              :value="type"
              :label="chartTypeNameMap[type]"
            />
          </Select>
        </FormItem>
        <FormItem
          v-if="currentChart.type === 'time'"
          label="Mode"
        >
          <RadioGroup v-model="currentChart.timeSeriesOptions.mode">
            <Radio label="bar">
              Bar
            </Radio>
            <Radio label="line">
              Line
            </Radio>
          </RadioGroup>
        </FormItem>
        <FormItem label="X-Axis">
          <Select
            v-model="currentChart.columnX"
            @on-change="autoFillTitle"
          >
            <Option
              v-for="(column, index) in xColumns"
              :key="index"
              :value="column"
              :label="column"
            />
          </Select>
        </FormItem>
        <FormItem
          v-if="currentChart.type === 'time'"
          label="Group By"
        >
          <Select
            v-if="currentChart.type === 'time'"
            v-model="currentChart.timeSeriesOptions.groupBy"
          >
            <Option
              value="date"
              label="Date"
            />
            <Option
              value="month"
              label="Month"
            />
            <Option
              value="quarter"
              label="Quarter"
            />
          </Select>
        </FormItem>
        <FormItem
          v-if="showYAxisSelect"
          label="Y-Axis"
        >
          <div v-if="showDistinctCountCheckbox">
            <Checkbox
              v-model="currentChart.countAggrChecked"
              @on-change="autoFillTitle"
            />
            <span>Count</span>
          </div>
          <Select
            v-if="yColumns.length"
            v-model="currentChart.columnY"
            :disabled="disableYColumnSelect"
            @on-change="autoFillTitle"
          >
            <Option
              v-for="(column, index) in yColumns"
              :key="index"
              :value="column"
              :label="column"
            />
          </Select>
        </FormItem>
        <FormItem
          v-if="currentChart.type === 'histogram'"
          label="Bucket Size"
        >
          <InputNumber
            v-model="currentChart.histogramOptions.binSize"
            :min="1"
          />
        </FormItem>
        <FormItem label="Title">
          <Input
            v-model="currentChart.title"
            @on-change="() => currentChart.userFilledInTitle = true"
          />
        </FormItem>
      </Form>
    </Sider>
    <Layout :style="{ backgroundColor: 'var(--color-white)' }">
      <Content>
        <Tabs
          :key="chartArr.length"
          v-model="currentChartIndex"
          name="chart-tabs"
          type="card"
          @on-click="onTabClick"
          @on-tab-remove="removeChart"
        >
          <TabPane
            v-for="(chart, index) in chartArr"
            :key="index"
            :label="chart.title || `New Chart ${index + 1}`"
            :closable="chartArr.length > 1"
            tab="chart-tabs"
          />
          <TabPane
            label="+"
            tab="chart-tabs"
          />
        </Tabs>
        <div class="chart-tab-content">
          <Spin
            v-if="chartLoading"
            fix
            size="large"
          />
          <div
            v-for="(chart, index) in chartArr"
            v-else
            v-show="index === currentChartIndex"
            :key="index"
            style="height: 100%"
          >
            <BarChart
              v-if="showBarChart"
              :data="chart.data"
              :x-axis="getXAxisObj(chart)"
              :y-axis="getYAxisObj(chart)"
              :show-slider="shouldShowSlider(chart)"
              :title="chart.title"
              show-download
            />
            <PieChart
              v-else-if="chart.type === 'pie'"
              :data="chart.data"
              :series-name="chart.columnX"
              :title="chart.title"
              show-download
            />
            <LineChart
              v-else-if="showLineChart"
              :data="chart.data"
              :x-axis="getXAxisObj(chart)"
              :y-axis="getYAxisObj(chart)"
              :show-slider="shouldShowSlider(chart)"
              :title="chart.title"
              add-min-max-cushion
              show-download
            />
            <Histogram
              v-else-if="chart.type === 'histogram'"
              :data="chart.data"
              :x-axis="getXAxisObj(chart)"
              :title="chart.title"
              :bin-size="chart.histogramOptions.binSize"
            />
          </div>
        </div>
      </Content>
    </Layout>
  </Layout>
</template>

<script>
import BarChart from '@/components/helper-components/chart-components/BarChart'
import PieChart from '@/components/helper-components/chart-components/PieChart'
import LineChart from '@/components/helper-components/chart-components/LineChart'
import Histogram from '@/components/helper-components/chart-components/Histogram'

import axios from 'axios'
import moment from 'moment-timezone'
import _ from 'lodash'

const DATE_TIME_TYPES = [
  'date',
  'datetime',
  'time',
  'timestamp',
  'timestamp_ltz',
  'timestamp_ntz',
  'timestamp_tz'
]

const QUARTER_RANGES = [
  { quarter: 1, start: 0, end: 2 },
  { quarter: 2, start: 3, end: 5 },
  { quarter: 3, start: 6, end: 8 },
  { quarter: 4, start: 9, end: 11 }
]

const SHORT_FORM_MONTH_NAMES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec'
]

export default {
  components: {
    BarChart,
    PieChart,
    LineChart,
    Histogram
  },

  model: {
    prop: 'charts',
    event: 'update'
  },

  props: {
    charts: {
      type: Array,
      default: () => []
    },
    queryId: {
      type: String,
      default: ''
    },
    typeObj: {
      type: Object,
      default: () => null
    },
    minHeight: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      colSiderWidth: 240,
      chartArr: this.charts,
      currentChartIndex: 0,
      chartLoading: true,
      tabChange: false,
      chartTypeNameMap: {
        bar: 'Bar Chart',
        line: 'Line Chart',
        pie: 'Pie Chart',
        time: 'Time Series',
        histogram: 'Histogram'
      }
    }
  },

  computed: {
    currentChart () {
      return this.chartArr[this.currentChartIndex] || {}
    },

    columnTypes () {
      return this.typeObj || null
    },

    tableColumns () {
      return this.typeObj ? Object.keys(this.typeObj) : []
    },

    xColumns () {
      const chartType = this.currentChart.type

      switch (chartType) {
        case 'time':
          return this.tableColumns.filter(column => DATE_TIME_TYPES.includes(this.columnTypes[column]))
        case 'histogram':
          return this.tableColumns.filter(column => this.columnTypes[column] === 'fixed')
        default:
          return this.tableColumns
      }
    },

    yColumns () {
      const chartType = this.currentChart.type

      switch (chartType) {
        case 'bar':
        case 'line':
        case 'time':
          return this.tableColumns.filter(column => this.columnTypes[column] === 'fixed')
        default:
          return []
      }
    },

    showBarChart () {
      const { type, timeSeriesOptions: { mode } } = this.currentChart

      return type === 'bar' || (type === 'time' && mode === 'bar')
    },

    showLineChart () {
      const { type, timeSeriesOptions: { mode } } = this.currentChart

      return type === 'line' || (type === 'time' && mode === 'line')
    },

    showDistinctCountCheckbox () {
      const { type } = this.currentChart

      return ['line', 'bar'].includes(type)
    },

    showYAxisSelect () {
      const { type } = this.currentChart

      return ['line', 'bar', 'time'].includes(type)
    },

    shouldAutoFillTitle () {
      const { columnX, columnY, countAggrChecked, type, title, userFilledInTitle } = this.currentChart
      return (
        columnX && (columnY || countAggrChecked || type === 'pie' || type === 'histogram') &&
        (!title || !userFilledInTitle)
      )
    },

    disableYColumnSelect () {
      const { countAggrChecked, type } = this.currentChart
      return countAggrChecked && [ 'bar', 'line' ].includes(type)
    },

    showIncompleteDataWarningMessage () {
      const { type, data } = this.currentChart

      if (type) {
        const limit = this.getChartTypeDataLimit(type)

        switch (type) {
          case 'pie':
            return data.labels && data.labels.length >= limit
          case 'bar':
          case 'line':
          case 'time':
          case 'histogram':
            return data.x && data.x.length >= limit
        }
      }

      return false
    },

    incompleteDataWarningMessage () {
      const { type } = this.currentChart

      if (type) {
        const chartLimit = this.getChartTypeDataLimit(type).toLocaleString('en-US')

        return `
          *Please note that the maximum number of data points that ${this.chartTypeNameMap[type]} can handle is ${chartLimit}.
          If your query result has a large amount of rows, it is possible that some rows will be left out of the calculation of the chart data.
        `
      }

      return ''
    }
  },

  watch: {
    queryId () {
      this.resetCharts()
    },

    chartArr: {
      handler (newChartArr) {
        this.$emit('update', newChartArr)
      },
      deep: true
    },

    'currentChart.columnX': {
      handler () {
        this.setCurrentChartData()
      }
    },

    'currentChart.columnY': {
      handler () {
        this.setCurrentChartData()
      }
    },

    'currentChart.countAggrChecked': {
      handler () {
        this.setCurrentChartData()
      }
    },

    'currentChart.type': {
      handler (newType, oldType) {
        const switchableTypes = ['bar', 'line']
        if (this.tabChange) {
          this.tabChange = false
        } else if (!this.tabChange && switchableTypes.includes(newType) && switchableTypes.includes(oldType)) {
          this.setCurrentChartData()
        } else {
          this.clearCurrentChartOptions()
        }
      }
    },

    'currentChart.timeSeriesOptions': {
      handler () {
        this.setCurrentChartData()
      },
      deep: true
    }
  },

  created () {
    if (!this.chartArr.length) {
      this.addEmptyChart()
    }
  },

  methods: {
    /**
     * Retrives the limit that the given chart type can handle. This means that this function returns the maximum
     * number of data points the chart type can support
     * @param {String} chartType
     * @return {Number}
     */
    getChartTypeDataLimit (chartType) {
      switch (chartType) {
        case 'pie':
          return 1000
        case 'bar':
        case 'line':
        case 'time':
          return 10000
        case 'histogram':
          return 100000
      }

      return 0
    },

    /**
     * Autofills the title for the current chart if both X Axis and Y Axis options are filled, and if
     * The user has not filled in their own title
     */
    autoFillTitle () {
      if (this.shouldAutoFillTitle) {
        let title = `${this.currentChart.columnX} vs ${this.currentChart.columnY}`

        if (this.currentChart.type === 'pie') {
          title = `${this.currentChart.columnX} Pie Chart`
        } else if (this.currentChart.type === 'histogram') {
          title = `${this.currentChart.columnX} Histogram`
        } else if (this.currentChart.countAggrChecked) {
          title = `${this.currentChart.columnX} Count`
        }

        this.$set(this.currentChart, 'title', title)
      }
    },

    /**
     * Cleans the column name so that it is better formatted for parsing
     * The column will replace spaces with underscores and make it upper case
     * @param {String} columnName
     * @return {String}
     */
    cleanColumnName (columnName) {
      return _.snakeCase(columnName).toUpperCase()
    },

    /**
     * Creates a query string used to get the count for a given column
     * @param {String} column
     * @return {String}
     */
    createColumnCountQuery (column) {
      return `
        SELECT "${column}", count(*)
        FROM (
          select * from
          table(result_scan('${this.queryId}'))
        )
        GROUP BY "${column}"
      `
    },

    /**
     * Creates a query string to get the sum for a given column
     * @param {String} sumColumn - The column we want to sum up
     * @param {String} groupByColumn - The column we want to group the sum up by
     * @return {String}
     */
    createColumnSumQuery (sumColumn, groupByColumn) {
      return `
        SELECT "${groupByColumn}", SUM("${sumColumn}") as ${this.cleanColumnName(`SUM_${sumColumn}`)}
        FROM (
          select * from
          table(result_scan('${this.queryId}'))
        )
        GROUP BY "${groupByColumn}"
      `
    },

    /**
     * Gathers the data used for charts that have 2 axis/columns
     * @param {Object} chart
     * @return {Array}
     */
    async getTwoColumnChartData (chart) {
      let data = this.getDefaultChartData()

      if (chart.columnX) {
        if (chart.countAggrChecked) {
          data = await this.getCountData(chart)
        } else if (this.columnTypes[chart.columnY] === 'fixed') {
          data = await this.getValueData(chart)
        }
      }

      return data
    },

    /**
     * Gets the chart data for the Pie chart
     * @return {Array}
     */
    async getCountData (chart) {
      const { columnX } = chart
      const chartData = this.getDefaultChartData()

      if (columnX) {
        try {
          const results = await this.runQuery(this.createColumnCountQuery(columnX), this.getChartTypeDataLimit(chart.type))

          results
            .sort((resultA, resultB) => new Date(resultA[columnX]) - new Date(resultB[columnX]))
            .forEach(result => {
              chartData.x.push(result[columnX])
              chartData.y.push(result[`COUNT(*)`])
            })

          return chartData
        } catch (error) {
          console.error(`Error while getting pie chart data: ${error.message}`)
        }
      }

      return Promise.resolve(chartData)
    },

    /**
     * Gets the chart data for X vs Y charts for a given chart object
     * @param {Object} chart
     * @return {Array}
     */
    async getValueData (chart) {
      const { columnX, columnY } = chart
      const chartData = this.getDefaultChartData()

      if (!columnX || !columnY) {
        return Promise.resolve(chartData)
      }

      const results = await this.runQuery(this.createColumnSumQuery(columnY, columnX), this.getChartTypeDataLimit(chart.type))

      results
        .sort((resultA, resultB) => new Date(resultA[columnX]) - new Date(resultB[columnX]))
        .forEach(result => {
          chartData.x.push(result[columnX])
          chartData.y.push(result[this.cleanColumnName(`SUM_${columnY}`)])
        })

      return chartData
    },

    /**
     * Determines if we should show the slider for the given chart
     * @param {Object} chart
     * @return {Boolean}
     */
    shouldShowSlider (chart) {
      return this.getXAxisType(chart) === 'time' || chart.type === 'time'
    },

    /**
     * Creates the quarter string for a given date. For example, January 15, 2000 belongs to Q1 2000
     * @param {String} date
     * @return {String}
     */
    getQuarterString (date) {
      const month = moment(date).month()
      const year = moment(date).year()
      const quarterRange = QUARTER_RANGES.filter(quarterRange => month >= quarterRange.start && month <= quarterRange.end).pop()

      return quarterRange ? `Q${quarterRange.quarter} ${year}` : ''
    },

    /**
     * Sorts the array (in place) of month strings
     * @param {Array} monthStrings
     */
    sortMonthStrings (monthStrings) {
      monthStrings.sort((monthStringA, monthStringB) => {
        const monthStringASplit = monthStringA.split(' ')
        const monthStringBSplit = monthStringB.split(' ')
        const monthA = monthStringASplit[0].toLowerCase()
        const monthB = monthStringBSplit[0].toLowerCase()
        const yearA = monthStringASplit[1]
        const yearB = monthStringBSplit[1]
        const yearDiff = yearA - yearB
        const monthIndexDiff = SHORT_FORM_MONTH_NAMES.findIndex(month => month === monthA) - SHORT_FORM_MONTH_NAMES.findIndex(month => month === monthB)

        return yearDiff !== 0 ? yearDiff : monthIndexDiff
      })
    },

    /**
     * Sorts the array (in place) of quarter strings
     * @param {Array} quarterStrings
     */
    sortQuarterStrings (quarterStrings) {
      quarterStrings.sort((quarterStringA, quarterStringB) => {
        const quarterStringASplit = quarterStringA.split(' ')
        const quarterStringBSplit = quarterStringB.split(' ')
        const quarterA = quarterStringASplit[0].toLowerCase()
        const quarterB = quarterStringBSplit[0].toLowerCase()
        const yearA = quarterStringASplit[1]
        const yearB = quarterStringBSplit[1]
        const yearDifference = yearA - yearB

        if (yearDifference !== 0) {
          return yearDifference
        }

        if (quarterA < quarterB) {
          return -1
        } else if (quarterA > quarterB) {
          return 1
        }

        return 0
      })
    },

    /**
     * Creates a hash mapping of dates to values based on results and chart settings
     * @param {Array} results
     * @param {Object} chart - Chart settings
     * @return {Object}
     */
    createDateTimeBucket (results, chart) {
      const { columnX, columnY, timeSeriesOptions: { groupBy } } = chart
      const dateTimeBucket = {}

      results.forEach(result => {
        const dateTimeMoment = moment(result[columnX])

        if (dateTimeMoment.isValid()) {
          let dateTimeKey = dateTimeMoment.format('YYYY-MM-DD')

          switch (groupBy) {
            case 'month':
              dateTimeKey = dateTimeMoment.format('MMM YYYY')
              break
            case 'quarter':
              dateTimeKey = this.getQuarterString(result[columnX])
              break
          }

          if (dateTimeBucket[dateTimeKey] === undefined) {
            dateTimeBucket[dateTimeKey] = result[this.cleanColumnName(`SUM_${columnY}`)]
          } else {
            dateTimeBucket[dateTimeKey] += result[this.cleanColumnName(`SUM_${columnY}`)]
          }
        }
      })

      return dateTimeBucket
    },

    /**
     * Gets the data needed for a time series chart
     * @param {Object} chart - Chart settings
     * @return {Promise} resolved to chart data
     */
    async getTimeSeriesData (chart) {
      const { columnX, columnY, timeSeriesOptions: { groupBy } } = chart
      const chartData = this.getDefaultChartData()

      if (!columnX || !columnY) {
        return Promise.resolve(chartData)
      }

      const results = await this.runQuery(this.createColumnSumQuery(columnY, columnX), this.getChartTypeDataLimit(chart.type))
      const dateTimeBucket = this.createDateTimeBucket(results, chart)
      const sortedDateTimeKeys = Object.keys(dateTimeBucket)

      switch (groupBy) {
        case 'month':
          this.sortMonthStrings(sortedDateTimeKeys)
          break
        case 'quarter':
          this.sortQuarterStrings(sortedDateTimeKeys)
          break
        default:
          sortedDateTimeKeys.sort((dateTimeKeyA, dateTimeKeyB) => moment(dateTimeKeyA).valueOf() - moment(dateTimeKeyB).valueOf())
      }

      sortedDateTimeKeys.forEach(dateTimeKey => {
        chartData.x.push(dateTimeKey)
        chartData.y.push(dateTimeBucket[dateTimeKey])
      })

      return chartData
    },

    /**
     * Gets the data needed for a pie chart
     * @param {Object} chart
     * @return {Promise} resolved to chart data
     */
    async getPieData (chart) {
      const chartData = await this.getCountData(chart)

      return {
        labels: chartData.x,
        values: chartData.y
      }
    },

    /**
     * Gets the data needed for histogram
     * @param {Object} chart
     * @return {Promise} resolved to chart data
     */
    async getHistogramData (chart) {
      const { columnX } = chart
      const chartData = this.getDefaultChartData()

      if (!columnX) {
        return Promise.resolve(chartData)
      }

      const results = await this.runQuery(this.createColumnCountQuery(columnX), this.getChartTypeDataLimit(chart.type))

      results.forEach(result => {
        for (let i = 0; i < result[`COUNT(*)`]; i++) {
          chartData.x.push(result[columnX])
        }
      })

      return chartData
    },

    /**
     * Invoked when a tab is clicked on
     * @param {Number} tabIndex
     */
    onTabClick (tabIndex) {
      if (tabIndex + 1 > this.chartArr.length) {
        this.addChartTab()
      } else {
        this.selectChartTab(tabIndex)
      }
    },

    /**
     * Invoked when a chart tab is cliked on
     * @param {Number} tabIndex
     */
    selectChartTab (tabIndex) {
      this.tabChange = true
      this.currentChartIndex = tabIndex
    },

    /**
     * Adds a chart tab
     */
    addChartTab () {
      this.addEmptyChart()
      this.$nextTick(() => {
        this.selectChartTab(this.chartArr.length - 1)
      })
    },

    /**
     * Resets the chart array to default
     */
    resetCharts () {
      this.chartArr = []
      this.addEmptyChart()
    },

    /**
     * Adds an empty chart object to the chart array
     */
    addEmptyChart () {
      this.chartArr.push({
        type: 'bar',
        title: '',
        columnX: '',
        columnY: '',
        userFilledInTitle: false,
        countAggrChecked: false,
        data: {},
        timeSeriesOptions: {
          mode: 'bar',
          groupBy: 'date'
        },
        histogramOptions: {
          binSize: 10
        }
      })
    },

    /**
     * Gets the X axis type which is based on the Column X Type of the given chart
     * @param {Object} chart
     * @return {String}
     */
    getXAxisType (chart) {
      const { type, timeSeriesOptions: { groupBy } } = chart
      const columnXType = this.columnTypes[chart.columnX]

      if ((type !== 'time' && DATE_TIME_TYPES.includes(columnXType)) || (type === 'time' && groupBy === 'date')) {
        return 'time'
      }

      return 'category'
    },

    /**
     * Gets the X Axis object for the given chart which is used to process the data needed
     * to display the X axis
     * @param {Object} chart
     * @return {Object}
     */
    getXAxisObj (chart) {
      if (chart.columnX) {
        return {
          name: chart.columnX,
          type: this.getXAxisType(chart)
        }
      }

      return null
    },

    /**
     * Gets the Y Axis object for the given chart which is used to process the data needed
     * to display to Y axis
     * @param {Object} chart
     * @return {Object}
     */
    getYAxisObj (chart) {
      if (chart.columnY || chart.countAggrChecked || chart.type === 'histogram') {
        return {
          name: (chart.countAggrChecked || chart.type === 'histogram') ? 'COUNT' : chart.columnY,
          type: 'value'
        }
      }

      return null
    },

    /**
     * Creates and returns default chart data
     * @return {Object}
     */
    getDefaultChartData () {
      return { x: [], y: [] }
    },

    /**
     * Removes a chart and selects the chart tab preceding it
     * @param {Number} chartIndex
     */
    removeChart (chartIndex) {
      this.chartArr.splice(chartIndex, 1)
      const targetTabIndex = chartIndex - 1 >= 0 ? chartIndex - 1 : 0

      this.selectChartTab(targetTabIndex)
    },

    /**
     * Clears all of the current chart options
     */
    clearCurrentChartOptions () {
      this.$set(this.currentChart, 'columnX', '')
      this.$set(this.currentChart, 'columnY', '')
      this.$set(this.currentChart, 'countAggrChecked', false)
      this.$set(this.currentChart, 'data', {})
    },

    /**
     * Sets data property for the currently selected chart
     */
    async setCurrentChartData () {
      this.chartLoading = true
      const { type } = this.currentChart
      let chartData = {}

      switch (type) {
        case 'pie':
          chartData = await this.getPieData(this.currentChart)
          break
        case 'histogram':
          chartData = await this.getHistogramData(this.currentChart)
          break
        case 'line':
        case 'bar':
          chartData = await this.getTwoColumnChartData(this.currentChart)
          break
        case 'time':
          chartData = await this.getTimeSeriesData(this.currentChart)
          break
      }

      this.$set(this.currentChart, 'data', chartData)
      this.chartLoading = false
    },

    /**
     * Runs the given query
     * @param {String} query
     * @param {Number} limit
     * @return {Promise} - resolves to query result
     */
    async runQuery (query, limit = 100000) {
      try {
        const result = (await axios({
          method: 'post',
          url: `${process.env.VUE_APP_API_URI}/snowflake/queries/stream`,
          timeout: 1000 * 60 * 5,
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            query,
            limit
          }
        })).data

        if (result.error) {
          throw new Error(`Error while running query for charts: ${result.error.message}`)
        }

        return result
      } catch (error) {
        console.error(error.message)
        throw error
      }
    }
  }
}
</script>

<style scoped>
  .chart-properties-section {
    background-color: var(--color-white);
    padding: 16px;
  }

  .chart-tab-content {
    position: relative;
    height: 100%;
    border-left: 1px solid var(--color-gray-border);
  }
</style>
